import { useTranslation } from "react-i18next";
import {
  FormikDocument,
  VehicleType,
  DataEntryVehicleStepSectionPropsWithStep,
  FormikStep,
  VehicleDocumentMapper,
} from "src/types";
import {
  useDeleteVehicleDocumentsDraft,
  useGetVehicleDocumentsDraft,
  usePostVehicleDocumentsDraft,
} from "src/networking";
import { useDraftIDStore } from "src/storage/formik/formik-draft.store";

import "./vehicle-impact-points-grid.scss";
import { useEffect } from "react";

export const useDataEntryVehicleStepVehicleDamages = ({
  step,
  formikFieldBaseName,
  formikVehicleValues,
  formik,
}: DataEntryVehicleStepSectionPropsWithStep) => {
  const { t } = useTranslation();
  const { crossGate: deleteVehicleDocument } = useDeleteVehicleDocumentsDraft();
  const { crossGate: postVehicleDocument } = usePostVehicleDocumentsDraft();
  const { crossGate: getVehicleDocumentsDraft } = useGetVehicleDocumentsDraft();

  const draftId = useDraftIDStore((state) => state.draftID);
  const vehicleDocumentKey = `${formikFieldBaseName}.vehicleDamages.documents`;
  const vehicleType =
    step == FormikStep.VEHICLE_A
      ? VehicleType.VEHICLE_A
      : VehicleType.VEHICLE_B;
  const vehicleTypeDamagesLetter =  step == FormikStep.VEHICLE_A ? "A" : "B"

  //Fetch documents drafted
  useEffect(() => {
    const fetchDocuments = () => {
      if (!draftId) return null;
      getVehicleDocumentsDraft(
        {
          uriParams: {
            idBozza: draftId,
          },
        },
        {
          onSuccess: (response) => {
            const documents = response.documenti;

            if (!documents || documents.length == 0) return;

            const mappedDocuments = documents
              .filter((document) => document.tipoVeicolo == vehicleType)
              .map((document) => VehicleDocumentMapper.fromDTO(document));

            formik.setFieldValue(vehicleDocumentKey, [...mappedDocuments]);
          },
          onFailure: (error) => {
            console.error("Error fetching vehicle documents:", error);
          },
        }
      );
    };

    fetchDocuments();
  }, []);

  // Upload file API
  const handleUploadFile = async (
    file: File
  ): Promise<FormikDocument | null> => {
    if (!draftId) return null;

    const formData = new FormData();
    formData.append("documento", file);

    return new Promise((resolve, reject) => {
      postVehicleDocument(
        {
          queryParams: {
            idBozza: draftId,
            tipoVeicolo: vehicleType,
            nome: file.name,
          },
          body: formData,
        },
        {
          onSuccess: (response) => {
            const uploadedDocument = VehicleDocumentMapper.fromDTO(response);
            resolve(uploadedDocument);
          },
          onFailure: (error) => {
            console.error(
              "Error uploading vehicle document: " + file.name,
              error
            );
            reject(error);
          },
        }
      );
    });
  };

  // Delete file API
  const handleDeleteFile = async (documentId: string): Promise<boolean> => {
    if (!draftId || !documentId) return false;

    let result = false;

    await deleteVehicleDocument(
      {
        uriParams: {
          idDocumento: documentId,
        },
        headers: {
          secretKey: draftId,
        },
      },
      {
        onSuccess: () => {
          result = true;
        },
        onFailure: (error) => {
          console.error("Error deleting vehicle document:", error);
        },
      }
    );

    return result;
  };

  // Add documents at Formik
  const handleDocumentsAdded = (documents: FormikDocument[]) => {
    const currentDocuments = formikVehicleValues.vehicleDamages.documents || [];
    formik.setFieldValue(vehicleDocumentKey, [
      ...currentDocuments,
      ...documents,
    ]);
  };

  // Remove document from Formik
  const handleDocumentRemoved = (documentId: string) => {
    const currentDocuments = formikVehicleValues.vehicleDamages.documents || [];
    const updatedDocuments = currentDocuments.filter(
      (doc) => doc.documentId !== documentId
    );
    formik.setFieldValue(vehicleDocumentKey, updatedDocuments);
  };

  const documents = formikVehicleValues.vehicleDamages.documents || [];

  return {
    documents,
    vehicleTypeDamagesLetter,
    t,
    handleUploadFile,
    handleDeleteFile,
    handleDocumentsAdded,
    handleDocumentRemoved,
  };
};
